/**
 * TrendingNowRail — products tagged "bestseller" or "trending" surface
 * here. Falls back to "newest" when tag metadata is sparse.
 */
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SectionHeader, HorizontalRail, Skeleton } from '../../design';
import ProductCard from '../products/ProductCard';
import useNearbyProducts from '../../hooks/useNearbyProducts';
import useCartActions from '../../hooks/useCartActions';

const TRENDING_TAGS = new Set(['bestseller', 'trending', 'popular', 'top_picks', 'hot']);

const TrendingNowRail: React.FC<{ limit?: number }> = ({ limit = 12 }) => {
  const { loading, products } = useNearbyProducts({ storeLimit: 5, perStore: 30 });
  const cart = useCartActions();
  const navigate = useNavigate();

  const trending = useMemo(() => {
    const tagged = products.filter((p) =>
      (p.tags ?? []).some((t) => TRENDING_TAGS.has(String(t).toLowerCase())),
    );
    if (tagged.length >= 6) return tagged.slice(0, limit);
    // Fallback: newest first
    return [...products]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, limit);
  }, [products, limit]);

  if (!loading && trending.length === 0) return null;

  return (
    <section style={{ marginBottom: 24 }}>
      <SectionHeader
        title="🔥 Trending now"
        subtitle="What everyone's buying this week"
        accent="primary"
        actionLabel="See all"
        onAction={() => navigate('/catalog?sort=trending')}
      />
      {loading ? (
        <HorizontalRail ariaLabel="Trending now">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} style={{ width: 168 }}>
              <Skeleton.ProductCard />
            </div>
          ))}
        </HorizontalRail>
      ) : (
        <HorizontalRail ariaLabel="Trending now" itemMinWidth={168}>
          {trending.map((p) => (
            <ProductCard
              key={`${p.store_id}:${p.id}`}
              product={p}
              storeName={p.store_name}
              qty={cart.getQty(p)}
              adding={cart.isBusy(p)}
              onAdd={() => cart.add(p)}
              onIncrement={() => cart.increment(p)}
              onDecrement={() => cart.decrement(p)}
              variant="compact"
              badge={
                (p.tags ?? []).some((t) => String(t).toLowerCase() === 'bestseller')
                  ? { label: 'BESTSELLER', variant: 'accent' }
                  : null
              }
            />
          ))}
        </HorizontalRail>
      )}
    </section>
  );
};

export default TrendingNowRail;

